import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Row,
  Col,
  Table,
  Image,
  Typography,
  Space,
  message,
  Tag,
  Modal,
} from 'antd';
import { InboxOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, CameraOutlined, PictureOutlined, PlayCircleOutlined } from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import {
  createSwatchV2,
  listSwatchesV2,
  deleteSwatchV2,
  deleteSwatchesV2,
} from '../../services/aiImageMaskingApi';

import { processTryOn } from '../../services/aiImageMaskingApi';
const { Dragger } = Upload;
const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const SwatchesV2 = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [swatches, setSwatches] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lifestyleFile, setLifestyleFile] = useState(null);
  const [lifestylePreview, setLifestylePreview] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [tryingSwatchId, setTryingSwatchId] = useState(null);
  const [tryResult, setTryResult] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchSwatches = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, searchText = search) => {
      setLoading(true);
      try {
        const data = await listSwatchesV2({
          page,
          page_size: pageSize,
          search: searchText || undefined,
        });
        setSwatches(Array.isArray(data?.items) ? data.items : []);
        setPagination({
          page: data?.page || page,
          pageSize: data?.page_size || pageSize,
          total: data?.total || 0,
        });
      } catch (error) {
        message.error(error.message || 'Unable to fetch swatches');
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.pageSize, search]
  );

  useEffect(() => {
    const handler = setTimeout(() => fetchSwatches(1, pagination.pageSize, search), 350);
    return () => clearTimeout(handler);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSwatches();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (lifestylePreview) {
        URL.revokeObjectURL(lifestylePreview);
      }
    };
  }, [lifestylePreview]);

  const handleLifestyleSelection = (file) => {
    if (!file) {
      return;
    }
    if (lifestylePreview) {
      URL.revokeObjectURL(lifestylePreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setLifestyleFile(file);
    setLifestylePreview(previewUrl);
    setShowPhotoModal(false);
  };

  const handleLifestyleInput = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleLifestyleSelection(file);
    }
    // reset input so the same file can be selected again
    event.target.value = '';
  };

  const uploadProps = useMemo(
    () => ({
      multiple: false,
      fileList,
      beforeUpload: (file) => {
        setFileList([file]);
        return false;
      },
      onRemove: () => setFileList([]),
      accept: '.png,.jpg,.jpeg,.webp',
      maxCount: 1,
    }),
    [fileList]
  );

  const handleUpload = async (values) => {
    if (!fileList.length) {
      message.warning('Please attach an image');
      return;
    }
    setUploading(true);
    try {
      await createSwatchV2({
        file: fileList[0],
        name: values.name,
        description: values.description,
      });
      message.success('Swatch stored in the studio library');
      setFileList([]);
      form.resetFields();
      fetchSwatches(1, pagination.pageSize, search);
    } catch (error) {
      message.error(error.message || 'Unable to upload swatch');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRowKeys.length) return;
    setDeleting(true);
    try {
      if (selectedRowKeys.length === 1) {
        await deleteSwatchV2(selectedRowKeys[0]);
      } else {
        await deleteSwatchesV2(selectedRowKeys);
      }
      message.success(
        `Deleted ${selectedRowKeys.length} swatch${selectedRowKeys.length > 1 ? 'es' : ''}`
      );
      setSelectedRowKeys([]);
      fetchSwatches(1, pagination.pageSize, search);
    } catch (error) {
      message.error(error.message || 'Unable to delete swatches');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleTryout = async (swatch) => {
    if (!lifestyleFile) {
      setShowPhotoModal(true);
      return;
    }
    setTryingSwatchId(swatch.id);
    try {
      const response = await processTryOn({
        environmentFile: lifestyleFile,
        swatchId: swatch.id,
        prompt: '',
      });
      const preview =
        response?.preview_url ||
        response?.composited_image_url ||
        (response?.image_base64 ? `data:image/png;base64,${response.image_base64}` : null);
      setTryResult({
        swatch,
        preview,
        metadata: response,
      });
      if (preview) {
        message.success('Tryout generated');
      }
    } catch (error) {
      message.error(error.message || 'Unable to run tryout');
    } finally {
      setTryingSwatchId(null);
    }
  };

  const columns = [
    {
      title: 'Preview',
      dataIndex: 'blob_url',
      key: 'preview',
      width: 140,
      render: (url) =>
        url ? (
          <Image
            src={url}
            alt="Swatch preview"
            width={80}
            height={80}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            placeholder
          />
        ) : (
          <Tag color="default">Pending</Tag>
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Paragraph type="secondary" className="mb-0">
            {record?.description || '—'}
          </Paragraph>
        </Space>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 220,
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: 'Tryout',
      key: 'tryout',
      width: 220,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={() => handleTryout(record)}
          loading={tryingSwatchId === record.id}
        >
          {lifestyleFile ? 'Tryout' : 'Upload photo to Tryout'}
        </Button>
      ),
    },
  ];

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="swatches-v2" />
      <input
        type="file"
        accept="image/*"
        ref={galleryInputRef}
        style={{ display: 'none' }}
        onChange={handleLifestyleInput}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        onChange={handleLifestyleInput}
      />

      <Card
        className="shadow-sm mb-4"
        title="Lifestyle photo for Tryouts"
        extra={
          <Space>
            <Button onClick={() => setShowPhotoModal(true)}>
              {lifestyleFile ? 'Replace photo' : 'Upload photo'}
            </Button>
            {lifestyleFile && (
              <Button
                danger
                onClick={() => {
                  if (lifestylePreview) {
                    URL.revokeObjectURL(lifestylePreview);
                  }
                  setLifestyleFile(null);
                  setLifestylePreview(null);
                }}
              >
                Remove
              </Button>
            )}
          </Space>
        }
      >
        {lifestylePreview ? (
          <Image
            src={lifestylePreview}
            alt="Lifestyle preview"
            style={{ maxHeight: 280, objectFit: 'cover', borderRadius: 12 }}
          />
        ) : (
          <Paragraph type="secondary" className="mb-0">
            Upload or capture a lifestyle photo to enable instant tryouts from this page.
          </Paragraph>
        )}
      </Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={10}>
          <Card
            title="Swatches / Overlays uploader"
            className="shadow-sm"
            bodyStyle={{ padding: 24 }}
            extra={
              <Button icon={<ReloadOutlined />} onClick={() => fetchSwatches(1, pagination.pageSize, search)}>
                Refresh
              </Button>
            }
          >
            <Form form={form} layout="vertical" onFinish={handleUpload}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input placeholder="Enter swatch name" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: 'Description is required' }]}
              >
                <TextArea rows={3} placeholder="Short description" />
              </Form.Item>
              <Form.Item label="Swatch Image" required>
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag image to this area to upload</p>
                  <p className="ant-upload-hint">PNG/JPG/WebP up to 10MB</p>
                </Dragger>
              </Form.Item>
              <Button
                type="primary"
                size="large"
                block
                icon={<PlusOutlined />}
                htmlType="submit"
                loading={uploading}
              >
                Save to studio library
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title="Swatches / Overlays library"
            className="shadow-sm"
            bodyStyle={{ padding: 0 }}
            extra={
              <Space size="small" wrap>
                <Input
                  prefix={<SearchOutlined />}
                  allowClear
                  placeholder="Search swatches or overlays"
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 240, maxWidth: '100%' }}
                />
                <Button
                  danger
                  disabled={!selectedRowKeys.length}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete Selected
                </Button>
                {selectedRowKeys.length > 0 && (
                  <Tag color="red" style={{ margin: 0 }}>
                    {selectedRowKeys.length} selected
                  </Tag>
                )}
              </Space>
            }
          >
            <Table
              rowKey="id"
              columns={columns}
              dataSource={swatches}
              loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            pagination={{
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: false,
                onChange: (page) => fetchSwatches(page, pagination.pageSize, search),
              }}
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm mt-4">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Title level={4}>Studio Storage Workflow</Title>
            <Paragraph type="secondary">
              Each upload uses the account-specific connection string to create (or reuse) a container and push the
              image there securely.
            </Paragraph>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" size="small">
              <Text strong>Storage account:</Text>
              <Paragraph className="mb-0" type="secondary">
                Swatches live alongside try-on assets per tenant, making it easy to manage lifecycle and retention.
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>
      <Modal
        title="Select lifestyle photo"
        open={showPhotoModal}
        onCancel={() => setShowPhotoModal(false)}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<CameraOutlined />}
            type="primary"
            block
            onClick={() => cameraInputRef.current?.click()}
          >
            Take photo with camera
          </Button>
          <Button
            icon={<PictureOutlined />}
            block
            onClick={() => galleryInputRef.current?.click()}
          >
            Choose from gallery
          </Button>
        </Space>
      </Modal>
      <Modal
        title={tryResult?.swatch ? `Tryout · ${tryResult.swatch.name}` : 'Tryout preview'}
        open={!!tryResult}
        onCancel={() => setTryResult(null)}
        footer={null}
      >
        {tryResult?.preview ? (
          <Image
            src={tryResult.preview}
            alt="Tryout preview"
            style={{ width: '100%', borderRadius: 12 }}
          />
        ) : (
          <Paragraph type="secondary">Preview unavailable.</Paragraph>
        )}
        {tryResult?.metadata?.preview_url && (
          <Paragraph className="mt-3" type="secondary">
            Stored at {tryResult.metadata.preview_url}
          </Paragraph>
        )}
      </Modal>
      <Modal
        title={`Delete ${selectedRowKeys.length} swatch${selectedRowKeys.length > 1 ? 'es' : ''}?`}
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onOk={handleDelete}
        confirmLoading={deleting}
        okButtonProps={{ danger: true }}
        okText="Delete"
      >
        <p>
          This will permanently remove the selected swatch
          {selectedRowKeys.length > 1 ? 'es' : ''} from the studio library.
        </p>
      </Modal>
    </div>
  );
};

export default SwatchesV2;

